import { useSoftphone } from "@dialstack/sdk-react/softphone";
import { useEffect, useRef } from "react";

// A second call arriving mid-call used to ring at full volume in the user's
// ear, over the patient they were talking to (ENG-2012). The SDK's ringtone
// (`WebRingtone`, mounted inside `SoftphoneProvider`) has no prop, no volume
// and is not exported, so while a call is connected we mute it and play a
// quiet call-waiting beep of our own instead. A ring on an idle phone is left
// exactly as the SDK plays it.
//
// REMOVE this file once the SDK takes a `ringtone` option (asked of DialStack
// in ENG-2012) — it leans on two SDK internals, both pinned by the test:
//   1. the ringtone builds a fresh `AudioContext` each time ringing starts and
//      connects its output to `ctx.destination`;
//   2. `WebRingtone` renders AFTER `children`, so this hook's effects (we are
//      inside `children`) run before the ringtone's in the same commit.

type AudioContextConstructor = typeof AudioContext;

const BEEP_FREQUENCY_HZ = 440;
const BEEP_GAIN = 0.05;
const BEEP_SECONDS = 0.2;
const BEEP_GAP_SECONDS = 0.2;
export const BEEP_REPEAT_MS = 10_000;

// The real constructor while `captureNextAudioContext` has its wrapper in
// place, so our own beep never trips the wrapper meant for the SDK's ring.
let nativeAudioContext: AudioContextConstructor | null = null;

// Routes the NEXT AudioContext built on this page through a gain node we
// hold, and hands that node to `onCapture`. One-shot: the wrapper comes off on
// first use, on the next tick, or on cleanup — whichever is first.
export function captureNextAudioContext(
  onCapture: (volume: GainNode) => void
): () => void {
  const Native = window.AudioContext;
  if (!Native || nativeAudioContext) return () => undefined;
  nativeAudioContext = Native;

  const restore = () => {
    if (nativeAudioContext !== Native) return;
    window.AudioContext = Native;
    nativeAudioContext = null;
  };

  window.AudioContext = function CapturedAudioContext(
    options?: AudioContextOptions
  ) {
    restore();
    const ctx = new Native(options);
    try {
      const volume = ctx.createGain();
      volume.connect(ctx.destination);
      // `destination` is a prototype getter; an own property shadows it, so
      // the ringtone's `gain.connect(ctx.destination)` lands on our node.
      Object.defineProperty(ctx, "destination", { value: volume });
      onCapture(volume);
    } catch {
      // An untouched context rings at full volume — better than not at all.
    }
    return ctx;
  } as unknown as AudioContextConstructor;

  const timer = setTimeout(restore, 0);
  return () => {
    clearTimeout(timer);
    restore();
  };
}

// Two short, quiet tones now and every BEEP_REPEAT_MS until stopped. Plays on
// the default output device. Silently does nothing without WebAudio.
export function startCallWaitingBeep(): () => void {
  const Native = nativeAudioContext ?? window.AudioContext;
  if (!Native) return () => undefined;
  let ctx: AudioContext;
  try {
    ctx = new Native();
  } catch {
    return () => undefined;
  }

  const beepTwice = () => {
    [0, BEEP_SECONDS + BEEP_GAP_SECONDS].forEach((offset) => {
      const startAt = ctx.currentTime + offset;
      const tone = ctx.createOscillator();
      const volume = ctx.createGain();
      tone.frequency.value = BEEP_FREQUENCY_HZ;
      volume.gain.value = BEEP_GAIN;
      tone.connect(volume);
      volume.connect(ctx.destination);
      tone.start(startAt);
      tone.stop(startAt + BEEP_SECONDS);
    });
  };

  beepTwice();
  const timer = setInterval(beepTwice, BEEP_REPEAT_MS);
  return () => {
    clearInterval(timer);
    ctx.close().catch(() => undefined);
  };
}

export default function useCallWaitingRing() {
  const { calls, incomingRinging } = useSoftphone();
  const onACall = calls.some(
    (call) => call.state === "active" || call.state === "held"
  );
  const onACallRef = useRef(onACall);
  onACallRef.current = onACall;

  // Hold the SDK ring's volume from the moment it starts, not only when a call
  // is already connected: two calls can ring an idle phone together, and the
  // ring keeps going (never restarts) once the first of them is answered.
  const ringVolumeRef = useRef<GainNode | null>(null);
  useEffect(() => {
    if (!incomingRinging) {
      ringVolumeRef.current = null;
      return undefined;
    }
    return captureNextAudioContext((volume) => {
      ringVolumeRef.current = volume;
      volume.gain.value = onACallRef.current ? 0 : 1;
    });
  }, [incomingRinging]);

  useEffect(() => {
    if (ringVolumeRef.current)
      ringVolumeRef.current.gain.value = onACall ? 0 : 1;
  }, [onACall]);

  const callIsWaiting = incomingRinging && onACall;
  useEffect(
    () => (callIsWaiting ? startCallWaitingBeep() : undefined),
    [callIsWaiting]
  );
}
